import { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Alert,
  LinearProgress,
  Box,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import AuthContext from '../AuthContext';
import { apiFetch } from '../api';
import GraphQcViewer from '../components/GraphQcViewer';
import { useTheme } from '@mui/material/styles';

// testing on http://localhost:3000/logs/1/qc

const QC_REVIEW_WINDOW_SECONDS = 48 * 3600;

const parseIsoDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatCountdown = (target) => {
  if (!(target instanceof Date) || Number.isNaN(target.getTime())) return '';
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return '';
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return days > 0 ? `${days}d ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
};

const extractQcAccess = (logData) => {
  if (!logData || typeof logData !== 'object') {
    return {
      availableAfter: null,
      qcViewedAt: null,
      reviewWindowSeconds: QC_REVIEW_WINDOW_SECONDS,
      expiresAt: null,
      statusHint: null,
    };
  }

  const access =
    logData.qc_access || logData.ownership || logData.purchase_access || {};
  const availableAfter = parseIsoDate(
    access.available_after || logData.available_after
  );
  const qcViewedAt = parseIsoDate(access.qc_viewed_at || logData.qc_viewed_at);
  const reviewWindowSeconds = Number.isFinite(access.qc_expiry_seconds)
    ? Number(access.qc_expiry_seconds)
    : Number.isFinite(access.review_window_seconds)
    ? Number(access.review_window_seconds)
    : QC_REVIEW_WINDOW_SECONDS;
  const expiresAt =
    qcViewedAt && Number.isFinite(reviewWindowSeconds)
      ? new Date(qcViewedAt.getTime() + reviewWindowSeconds * 1000)
      : null;
  const statusHint = access.status || access.qc_access_status || null;

  return {
    availableAfter,
    qcViewedAt,
    reviewWindowSeconds,
    expiresAt,
    statusHint,
  };
};

const determineQcStatus = (access, now = new Date()) => {
  if (!access) return 'loading';
  if (access.statusHint === 'expired') return 'expired';
  if (access.statusHint === 'waiting') return 'waiting';
  if (access.statusHint === 'viewed') return 'viewed';
  if (access.availableAfter && access.availableAfter > now) return 'waiting';
  if (access.qcViewedAt) {
    if (access.expiresAt && access.expiresAt <= now) return 'expired';
    return 'viewed';
  }
  return 'available';
};

export default function QcViewPage() {
  const { id } = useParams();
  const [log, setLog] = useState(null);
  const [graphs, setGraphs] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [error, setError] = useState(null);
  const [accessInfo, setAccessInfo] = useState(null);
  const [qcStatus, setQcStatus] = useState('loading');
  const [countdown, setCountdown] = useState('');
  const { token } = useContext(AuthContext);
  const theme = useTheme();

  useEffect(() => {
    const prevDocBg = document.documentElement.style.backgroundColor;
    const prevBodyBg = document.body.style.backgroundColor;
    const prevBodyColor = document.body.style.color;
    document.documentElement.style.backgroundColor = '#000';
    document.body.style.backgroundColor = '#000';
    document.body.style.color = '#fff';
    return () => {
      document.documentElement.style.backgroundColor = prevDocBg || '';
      document.body.style.backgroundColor = prevBodyBg || '';
      document.body.style.color = prevBodyColor || '';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadLog = async () => {
      setError(null);
      setLog(null);
      setAccessInfo(null);
      setGraphs(null);
      try {
        const logResp = await apiFetch(`/api/logs/${id}/`, {
          headers: token ? { Authorization: `Token ${token}` } : {},
        });
        if (!logResp.ok) {
          throw new Error('Failed to fetch log');
        }
        const logData = await logResp.json();
        if (cancelled) return;
        setLog(logData);
        const access = extractQcAccess(logData);
        setAccessInfo(access);
        const status = determineQcStatus(access);
        setQcStatus(status);
        if (status !== 'available') {
          setGraphs([]);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      }
    };

    loadLog();
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  useEffect(() => {
    if (qcStatus !== 'available' || !log) {
      return;
    }
    let cancelled = false;
    const loadGraphs = async () => {
      try {
        const resp = await apiFetch(`/api/logs/${id}/qc-graphs/`, {
          headers: token ? { Authorization: `Token ${token}` } : {},
        });
        if (!resp.ok) {
          if (resp.status === 403) {
            let detail = 'QC access is not available yet.';
            try {
              const raw = await resp.text();
              if (raw) {
                try {
                  const parsed = JSON.parse(raw);
                  if (parsed?.detail) {
                    detail = parsed.detail;
                  } else if (typeof parsed === 'string') {
                    detail = parsed;
                  }
                } catch (_) {
                  detail = raw;
                }
              }
            } catch (_) {
              // ignore parsing issues
            }
            if (!cancelled) {
              const lowered = detail.toLowerCase();
              if (lowered.includes('expired')) {
                setQcStatus('expired');
              } else if (lowered.includes('not yet') || lowered.includes('available')) {
                setQcStatus('waiting');
              } else if (lowered.includes('viewed') || lowered.includes('already')) {
                setQcStatus('viewed');
              } else {
                setQcStatus('waiting');
              }
              setError(null);
            }
            return;
          }
          throw new Error('Failed to fetch QC graphs');
        }
        const data = await resp.json();
        if (!cancelled) {
          setGraphs(Array.isArray(data.graphs) ? data.graphs : []);
          setTabIndex(0);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      }
    };

    loadGraphs();
    return () => {
      cancelled = true;
    };
  }, [qcStatus, log, id, token]);

  useEffect(() => {
    if (qcStatus === 'waiting' || qcStatus === 'viewed' || qcStatus === 'expired') {
      setGraphs([]);
                <Paper sx={{ p: 4, width: { xs: '100%', sm: 700 }, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', position: 'relative', zIndex: 2 }}>
                  <Typography variant="h4" component="h2" sx={{ color: 'inherit' }}>
                    {log
                      ? (log.API || log.api || log.api_number || `Log ${id}`)
                      : 'curve QC'}
                  </Typography>
                </Paper>
    }
  }, [qcStatus]);

  useEffect(() => {
    if (!accessInfo) {
      setCountdown('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      if (qcStatus === 'waiting' && accessInfo.availableAfter) {
        if (accessInfo.availableAfter <= now) {
          setCountdown('');
          setQcStatus((prev) => (prev === 'waiting' ? 'available' : prev));
        } else {
          setCountdown(formatCountdown(accessInfo.availableAfter));
        }
        return;
      }
      if (qcStatus === 'viewed' && accessInfo.expiresAt) {
        if (accessInfo.expiresAt <= now) {
          setCountdown('');
          setQcStatus('expired');
        } else {
          setCountdown(formatCountdown(accessInfo.expiresAt));
        }
        return;
      }
      setCountdown('');
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [accessInfo, qcStatus]);

  if (error) {
    return (
      <Box sx={{ bgcolor: '#000', color: '#fff', minHeight: '100vh' }}>
        <Container sx={{ py: 4, bgcolor: 'transparent', color: 'inherit' }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  const isLoading = !log || (qcStatus === 'available' && graphs === null);

  if (isLoading) {
    return (
      <Box sx={{ bgcolor: '#000', color: '#fff', minHeight: '100vh' }}>
        <Container sx={{ py: 4, bgcolor: 'transparent', color: 'inherit' }}>
          <LinearProgress />
        </Container>
      </Box>
    );
  }

  const hasUniqueGraphIds =
    new Set(graphs.map((g) => g.id)).size === graphs.length;

  const logTitle =
    log.visible_name || (log.file ? log.file.split('/').pop() : `Log ${id}`);

  return (
    <Box sx={{ bgcolor: '#000', color: '#fff', minHeight: '100vh' }}>
      <Container sx={{ py: 4, bgcolor: 'transparent', color: 'inherit' }}>
        <Box sx={theme.mixins.toolbar} />

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh', mb: 4 }}>
          <Paper sx={{ p: 4, width: { xs: '100%', sm: 700 }, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" component="h2" sx={{ color: 'inherit' }}>
              {log
                ? `${log.API || log.api || log.api_number || `Log ${id}`} - Curve Data QC`
                : 'Curve Data QC'}
            </Typography>
          </Paper>
        </Box>
        
        {qcStatus === 'waiting' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            QC content unlocks
            {countdown
              ? ` in ${countdown}`
              : accessInfo?.availableAfter
              ? ` at ${accessInfo.availableAfter.toLocaleString()}`
              : ' soon'}
            .
          </Alert>
        )}

        {qcStatus === 'viewed' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            QC review was previously accessed
            {accessInfo?.qcViewedAt
              ? ` on ${accessInfo.qcViewedAt.toLocaleString()}`
              : ''}
            .
            {countdown
              ? ` The review window closes in ${countdown}.`
              : ' Access is currently locked.'}
          </Alert>
        )}

        {qcStatus === 'expired' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            QC access has expired
            {accessInfo?.qcViewedAt
              ? ` (first viewed on ${accessInfo.qcViewedAt.toLocaleString()})`
              : ''}
            . Please contact support if you need additional access.
          </Alert>
        )}

        {qcStatus === 'available' ? (
          graphs.length === 0 ? (
            <Alert severity="info">No QC graphs available.</Alert>
          ) : (
            <>
              <Tabs
                value={tabIndex}
                onChange={(_, newValue) => setTabIndex(newValue)}
                aria-label="QC graph tabs"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  mb: 2,
                  '& .MuiTab-root': {
                    ...theme.typography.qcControl,
                    '&.Mui-selected': theme.typography.qcControl,
                  },
                }}
              >
              </Tabs>
              <Paper sx={{ 
                  p: 2, 
                  mb: 4, 
                  ml: -25, 
                  bgcolor: 'transparent', 
                  color: 'inherit', 
                  borderColor: '#fff', 
                  borderStyle: 'solid', 
                  borderWidth: 1,
                  width: 'calc(100% + 400px)',
                }}>
                <GraphQcViewer graph={graphs[tabIndex]} />
              </Paper>
            </>
          )
        ) : null}
      </Container>
    </Box>
  );
}